<template>
  <li>
    <div class="row" @click="handleClick" :class="{ clickable: isDir }">
      <span class="icon" v-if="isDir">
        <svg v-if="expanded" class="svg-icon dir-open" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 6h-8l-2-2H4a2 2 0 00-2 2v2h18a2 2 0 011.84 2.77l-3.02 7.05A2 2 0 0016.99 20H4a2 2 0 01-2-2V8h6l2 2h10V8a2 2 0 00-2-2z"/>
        </svg>
        <svg v-else class="svg-icon dir-closed" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z"/>
        </svg>
      </span>
      <span class="icon" v-else>
        <svg class="svg-icon" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M9,13V19H15V13H9M10,14H14V18H10V14M10,10H14V12H10V10Z"/>
        </svg>
      </span>
      <span class="name">{{ displayName }}</span>
    </div>
    <ul v-if="isDir && (expanded || expandAll)" class="children">
      <TreeViewNode 
        v-for="child in node.children" 
        :key="child.path" 
        :node="child" 
        :expand-all="expandAll"
        @select="emit('select', $event)"
      />
    </ul>
  </li>
</template>

<script setup lang="ts">
// Component name for export
defineOptions({
  name: 'TreeViewNode'
})
import { computed, ref } from 'vue'
import type { TreeNodeItem } from './TreeView.vue'
// import TreeNode from './TreeViewNode.vue' // Removendo referência circular

const props = defineProps<{ node: TreeNodeItem; expandAll?: boolean }>()
// Expansão padrão para pastas fixas: Documentos, Guides, Mandates, Patterns
const decodedNodeName = (() => { try { return decodeURIComponent(props.node.name) } catch { return props.node.name } })()
const shouldExpandByDefault = props.node.type === 'dir' && ['Documentos'].includes(decodedNodeName)
const expanded = ref(shouldExpandByDefault)
const isDir = computed(() => props.node.type === 'dir')
const emit = defineEmits<{ (e: 'select', node: TreeNodeItem): void }>()
const toggle = () => { if (isDir.value) expanded.value = !expanded.value }
const handleClick = () => {
  if (isDir.value) {
    toggle()
  } else {
    emit('select', props.node)
  }
}

const displayName = computed(() => {
  const raw = props.node.name
  let decoded = raw
  try { decoded = decodeURIComponent(raw) } catch { /* mantém original se inválido */ }
  if (props.node.type === 'dir') return decoded
  const i = decoded.lastIndexOf('.')
  return i > 0 ? decoded.slice(0, i) : decoded
})
</script>

<style scoped>
.row {
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.25rem;
}
.row.clickable { cursor: pointer; }
.row:hover { background: rgba(0,0,0,0.04); }
.icon { width: 1.25rem; text-align: center; }
.svg-icon { width: 1rem; height: 1rem; display: inline-block; fill: #0f172a; }
.dir-open { fill: #0f172a; }
.dir-closed { fill: #0f172a; }
.children { 
  list-style: none; 
  margin: 0; 
  padding-left: 1rem; 
  border-left: 1px solid var(--lwsa-gray-200);
}
.children > li { position: relative; }
.children > li::before {
  content: '';
  position: absolute;
  top: 0.9rem;
  left: -1rem;
  width: 1rem;
  border-top: 1px solid var(--lwsa-gray-200);
}
.name { user-select: none; color: #4b5563; font-weight: 500; }
.row.clickable .name { color: #374151; font-weight: 500; }
.row:hover .name { color: #1f2937; }
</style>


